import type { Feature, FeatureCollection, MultiPolygon, Polygon, Position } from 'geojson';
import turfArea from '@turf/area';
import { earthRadius } from '@turf/helpers';

const WGS84_SEMI_MAJOR_AXIS_IN_METERS = 6378137;
const WGS84_FLATTENING = 1 / 298.257223563;
const WGS84_ECCENTRICITY_SQUARED = WGS84_FLATTENING * (2 - WGS84_FLATTENING);
const DEGREES_TO_RADIANS = Math.PI / 180;

type ParcelGeometry = Polygon | MultiPolygon;
type ParcelGeoJSON = FeatureCollection<ParcelGeometry> | Feature<ParcelGeometry> | ParcelGeometry;

function rings(input: ParcelGeoJSON): Position[][] {
  switch (input.type) {
    case 'FeatureCollection':
      return input.features.flatMap(rings);
    case 'Feature':
      return input.geometry ? rings(input.geometry) : [];
    case 'Polygon':
      return input.coordinates;
    case 'MultiPolygon':
      return input.coordinates.flat();
  }
}

function middleLatitude(input: ParcelGeoJSON): number {
  let minimum = Infinity;
  let maximum = -Infinity;

  for (const ring of rings(input)) {
    for (const [, latitude] of ring) {
      if (latitude! < minimum) minimum = latitude!;
      if (latitude! > maximum) maximum = latitude!;
    }
  }

  return (minimum + maximum) / 2;
}

function earthRadiusAtLatitude(latitude: number): number {
  const eccentricityFactor = 1 - WGS84_ECCENTRICITY_SQUARED * Math.sin(latitude * DEGREES_TO_RADIANS) ** 2;
  const meridional = (WGS84_SEMI_MAJOR_AXIS_IN_METERS * (1 - WGS84_ECCENTRICITY_SQUARED)) / eccentricityFactor ** 1.5;
  const primeVertical = WGS84_SEMI_MAJOR_AXIS_IN_METERS / Math.sqrt(eccentricityFactor);

  return Math.sqrt(meridional * primeVertical);
}

/**
 * Area in square meters of a parcel geometry, matching the area the API stores for it.
 *
 * The API measures on the WGS84 ellipsoid while turf measures on a sphere of a single radius, which reads 0.3% low at
 * French latitudes. A parcel is small enough for the curvature of the ellipsoid to be constant over it, so scaling
 * turf's result by the radius of curvature at its latitude lands within a millimeter of the ellipsoidal area, without
 * needing a geodesic library.
 */
export function parcelArea(input: ParcelGeoJSON): number {
  const latitude = middleLatitude(input);
  if (!Number.isFinite(latitude)) return 0;

  const scale = earthRadiusAtLatitude(latitude) / earthRadius;

  return turfArea(input) * scale * scale;
}
